import {
  GoogleAuthProvider,
  sendPasswordResetEmail,
  confirmPasswordReset,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  type User
} from "firebase/auth";
import { auth } from "$lib/firebase/client";
import { AccountApiService } from "./ApiService";
import { UserFacingErrorMessage } from "$lib/lib/errors";

const PROJECT_ID = typeof window !== 'undefined'
  ? new URLSearchParams(window.location.search).get('projectId') || 'digital-pvp'
  : 'digital-pvp';

export class AuthenticationService {
  static instance: AuthenticationService | null = null;
  static STATE = {
    SUCCESS: "SUCCESS",
    ERROR: "ERROR",
    WRONG_PASSWORD: "WRONG_PASSWORD",
    PENDING: "PENDING",
    NEEDS_MIGRATION: "NEEDS_MIGRATION",
    IN_PROGRESS: "IN_PROGRESS",
    EMAIL_LINK_SENDING: "EMAIL_LINK_SENDING",
    EMAIL_LINK_SENT: "EMAIL_LINK_SENT"
  }

  private provider: GoogleAuthProvider;

  constructor() {
    this.provider = new GoogleAuthProvider();
    this.checkSignInWithEmailLink();
  }

  async maybeRedirectToMigration(user: User | null): Promise<boolean> {
    if (!user) {
      return true;
    }

    return !(user as User & { migrated?: boolean }).migrated;
  }

  async checkSignInWithEmailLink(): Promise<void> {
    if (!auth) return;
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email: string | null = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        email = window.bulmaPrompt
          ? await window.bulmaPrompt('Please provide your email for confirmation', '')
          : window.prompt('Please provide your email for confirmation', '');
      }
      if (email) {
        signInWithEmailLink(auth, email, window.location.href)
          .then(() => {
            window.localStorage.removeItem('emailForSignIn');
          })
          .catch((error: Error) => {
            console.error(error);
          });
      }
    }
  }

  async loginWithGoogle(): Promise<void> {
    if (!auth) throw new Error('Firebase Auth not initialised');
    await signInWithPopup(auth, this.provider);
  }

  async loginWithPassword(email: string, password: string): Promise<string> {
    if (!auth) throw new Error('Firebase Auth not initialised');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e: any) {
      if (e.code === "auth/wrong-password") {
        return AuthenticationService.STATE.WRONG_PASSWORD;
      }
      const errors = [
        "auth/wrong-password",
        "auth/quota-exceeded",
        "auth/timeout",
        "auth/too-many-requests",
        "auth/unverified-email",
        "auth/user-cancelled",
        "auth/user-not-found",
        "auth/user-disabled",
        "auth/missing-password",
        "auth/invalid-credential"
      ];
      let key = "errors.auth/unknown";
      let params: { code?: string } = { code: e.code };
      if (errors.includes(e.code)) {
        key = "errors." + e.code;
        params = {};
      }
      throw new UserFacingErrorMessage(key, params);
    }

    return AuthenticationService.STATE.SUCCESS;
  }

  async signUpWithPassword(email: string, password: string): Promise<void> {
    if (!auth) throw new Error('Firebase Auth not initialised');
    await createUserWithEmailAndPassword(auth, email, password);
  }

  async sendResetPasswordEmail(email: string): Promise<void> {
    if (!auth) throw new Error('Firebase Auth not initialised');
    await sendPasswordResetEmail(auth, email);
  }

  async confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    if (!auth) throw new Error('Firebase Auth not initialised');
    await confirmPasswordReset(auth, code, newPassword);
  }

  async loginWithEmail(email: string): Promise<boolean> {
    if (!auth) throw new Error('Firebase Auth not initialised');
    await sendSignInLinkToEmail(auth, email, {
      url: window.location.href,
      handleCodeInApp: true,
    });
    window.localStorage.setItem('emailForSignIn', email);
    return true;
  }

  static getInstance(): AuthenticationService {
    if (AuthenticationService.instance == null) {
      AuthenticationService.instance = new AuthenticationService();
    }
    return AuthenticationService.instance;
  }

  logIn(code: string): Promise<any> {
    const code_verifier = sessionStorage.getItem("code_verifier");
    const formData = new FormData();
    formData.append("code", code);
    formData.append("code_verifier", code_verifier as string);
    return new Promise((resolve, reject) => {
      fetch(`https://${this.getRedirectHost()}/wp-json/banshee/v1/token`, {
        method: "POST",
        body: formData,
      })
        .then((res) => res.json())
        .then((jwt) => {
          resolve(jwt);
        })
        .catch((e: Error) => {
          reject(e);
        });
    });
  }

  getRedirectHost(): string {
    switch (PROJECT_ID) {
      case "digital-pvp-prod":
        return "account.universalvoice.nl";
      case "digital-pvp":
        return "account-dev.universalvoice.nl";
      default:
        throw new Error("Running in unknown project");
    }
  }

  async sha256hex(data: string): Promise<string> {
    const msgUint8 = new TextEncoder().encode(data);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    return hashHex;
  }

  async generateCodeChallenge(): Promise<string> {
    let code_verifier = "";
    for (let i = 0; i < 128; i++) {
      code_verifier +=
        "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ".charAt(
          Math.floor(Math.random() * 64)
        );
    }
    sessionStorage.setItem("code_verifier", code_verifier);

    const code_challenge = await this.sha256hex(code_verifier);
    return code_challenge;
  }

  async redirectToWordpress(): Promise<void> {
    let url = window.location.href.split("?")[0];
    if (!url) {
      throw new Error("No url found");
    }
    let code_challenge = await this.generateCodeChallenge();
    window.location.href = `https://${this.getRedirectHost()}/redirect?url=${encodeURIComponent(
      url
    )}&code_challenge=${encodeURIComponent(code_challenge)}`;
  }
}
